'use client';

import React, { useState } from 'react';

interface Transaction {
  id: number;
  ref: string;
  type: 'booking' | 'payout' | 'refund' | 'fee';
  party: string;
  amount: string;
  date: string;
  method: string;
  status: 'completed' | 'pending' | 'failed';
}

const transactions: Transaction[] = [
  { id: 1, ref: 'TXN-0821-001', type: 'booking', party: 'Sarah Nakato', amount: '+UGX 2,500,000', date: 'Aug 20, 2026', method: 'Mobile Money', status: 'completed' },
  { id: 2, ref: 'TXN-0820-002', type: 'payout', party: 'James Ssekandi', amount: '-UGX 2,125,000', date: 'Aug 20, 2026', method: 'Bank Transfer', status: 'completed' },
  { id: 3, ref: 'TXN-0820-003', type: 'fee', party: 'Platform Fee', amount: '+UGX 375,000', date: 'Aug 20, 2026', method: 'Internal', status: 'completed' },
  { id: 4, ref: 'TXN-0818-004', type: 'booking', party: 'Sarah Nakato', amount: '+UGX 1,200,000', date: 'Aug 18, 2026', method: 'Visa Card', status: 'completed' },
  { id: 5, ref: 'TXN-0818-005', type: 'payout', party: 'Grace Achieng', amount: '-UGX 1,020,000', date: 'Aug 18, 2026', method: 'Mobile Money', status: 'pending' },
  { id: 6, ref: 'TXN-0402-006', type: 'refund', party: 'Moses Kato', amount: '-UGX 450,000', date: 'Apr 5, 2026', method: 'Visa Card', status: 'completed' },
];

const typeColors: Record<string, string> = {
  booking: 'bg-green-100 text-green-700',
  payout: 'bg-blue-100 text-blue-700',
  refund: 'bg-orange-100 text-orange-700',
  fee: 'bg-purple-100 text-purple-700',
};

const statusColors: Record<string, string> = {
  completed: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  failed: 'bg-red-100 text-red-600',
};

export default function AdminPayments() {
  const [filter, setFilter] = useState('all');

  const filtered = transactions.filter((t) => filter === 'all' || t.type === filter);

  return (
    <div>
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Gross Revenue (Aug)', value: 'UGX 96M', color: 'text-green-600', icon: '💰' },
          { label: 'Payouts Processed', value: 'UGX 81.6M', color: 'text-blue-600', icon: '💳' },
          { label: 'Platform Fees', value: 'UGX 14.4M', color: 'text-purple-600', icon: '📊' },
          { label: 'Pending Payouts', value: 'UGX 4.2M', color: 'text-amber-600', icon: '⏳' },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
            <div className="text-xl mb-1">{s.icon}</div>
            <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-5">
        {(['all', 'booking', 'payout', 'refund', 'fee'] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-xs font-semibold capitalize transition-all ${
              filter === f ? 'bg-[#4088C6] text-white' : 'bg-white text-gray-600 border border-gray-200 hover:border-[#4088C6]/40'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Transaction</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Party</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Method</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Amount</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-4 text-xs font-mono text-gray-500">{t.ref}</td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${typeColors[t.type]}`}>{t.type}</span>
                  </td>
                  <td className="px-5 py-4 text-sm text-gray-700">{t.party}</td>
                  <td className="px-5 py-4 text-sm text-gray-500">{t.method}</td>
                  <td className={`px-5 py-4 text-sm font-bold ${t.amount.startsWith('+') ? 'text-green-600' : 'text-red-500'}`}>{t.amount}</td>
                  <td className="px-5 py-4 text-sm text-gray-500">{t.date}</td>
                  <td className="px-5 py-4">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${statusColors[t.status]}`}>{t.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
